import * as yup from "yup";

import { formatCurrencyForPersistence } from "../../../../utils/Conversores";
import { EModule } from "../../../../enums";
import { ICityUpcomingConfigAndTerms } from "../../../../interfaces";

const COMMON_SCHEMA = {
    name: yup.string().required("Por favor, informe o nome do Orgão!"),
    is_in_test: yup.boolean(),
    is_address_required: yup.boolean(),
    // eslint-disable-next-line
    cnpj: yup.string().matches(/^([0-9]{3}\.?[0-9]{3}\.?[0-9]{3}\-?[0-9]{2}|[0-9]{2}\.?[0-9]{3}\.?[0-9]{3}\/?[0-9]{4}\-?[0-9]{2})$/, { message: "CNPJ inválido!" }).min(18, "CNPJ inválido!").max(18, "CNPJ inválido!").required("Por favor, informe o CNPJ do Município!"),
    city: yup.string().required("Por favor, informe o Município!"),
    uf: yup.string().required("Por favor, informe o Estado!"),
    points_config_description: yup.string(),
    tickets_config_description: yup.string(),
    logo_dark: yup.object().shape({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    logo_light: yup.object().shape({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    img_background: yup.object().shape({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    logo_county: yup.object().shape({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    terms_of_use: yup.object().shape({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    raffle_rules_file: yup.object({
        uid: yup.string().required(),
        name: yup.string().required(),
    }).notRequired().default(undefined),
    created_at: yup.string(),
};

interface IWrapperSchemaProps {
    points: {
        nfc_divider: number | undefined,
        nfs_divider: number | undefined,
        taxe_divider: number | undefined,
        nfs_max_amount: number | undefined,
        nfs_max_month_quantity_by_cnpj: number | undefined,
        nfc_max_amount: number | undefined,
        nfc_max_month_quantity_by_cnpj: number | undefined,
        taxe_max_amount: number | undefined,
        is_unlimited_nfs: boolean | undefined,
        is_unlimited_nfc: boolean | undefined,
        is_unlimited_by_cnpj_nfc: boolean | undefined,
        is_unlimited_by_cnpj_nfs: boolean | undefined,
        is_unlimited_taxe: boolean | undefined,
    },
    tickets: {
        nfc_divider: number | undefined,
        nfs_divider: number | undefined,
        taxe_divider: number | undefined,
        nfs_max_amount: number | undefined,
        nfs_max_month_quantity_by_cnpj: number | undefined,
        nfc_max_amount: number | undefined,
        nfc_max_month_quantity_by_cnpj: number | undefined,
        taxe_max_amount: number | undefined,
        tickets_from_partial_tax_payment: number | undefined,
        tickets_from_full_tax_payment: number | undefined,
        generate_ticket_on_first_invoice_import: boolean,
        is_unlimited_nfs: boolean | undefined,
        is_unlimited_nfc: boolean | undefined,
        is_unlimited_by_cnpj_nfc: boolean | undefined,
        is_unlimited_by_cnpj_nfs: boolean | undefined,
        is_unlimited_taxe: boolean | undefined,
    },
    type: "CREATE" | "EDIT",
    cityModules: EModule[],
    upcomingConfig: ICityUpcomingConfigAndTerms | undefined,
}

const maxAmountSchema = (
    module: EModule,
    unlimitedField: string,
    message: string
) =>
    yup.string().when(
        ['modules', unlimitedField],
        ([modules, isUnlimited], schema: any) => {
            if (isUnlimited === true || isUnlimited === undefined) {
                return schema.nullable().notRequired();
            }

            const hasModule = modules?.includes(module);

            return hasModule
                ? schema
                    .required(message)
                    .notOneOf(['0,00'], "Valor inválido!")
                : schema;
        }
    );


const maxQuantityByCnpjSchema = (
    module: EModule,
    unlimitedField: string,
    message: string
) =>
    yup.number().positive("Número precisa ser positivo!").when(
        ['modules', unlimitedField],
        ([modules, isUnlimited], schema: any) => {

            if (isUnlimited === true || isUnlimited === undefined) {
                return schema.nullable().notRequired();
            }

            const hasModule = modules?.includes(module);

            return hasModule
                ? schema.required(message)
                : schema;
        }
    );

export const wrapperSchema = ({
    type,
    points,
    tickets,
    cityModules,
    upcomingConfig,
}: IWrapperSchemaProps) =>
    yup.object().shape({
        ...COMMON_SCHEMA,
        nfc_points_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (!!upcomingConfig?.points_config?.find((cfg) => cfg.nfc_divider) && !points.nfc_divider) {
                return schema;
            }

            return [EModule.INVOICE_NFC, EModule.REWARD].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos pontos da NFC!") : schema;
        }),
        nfc_tickets_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.tickets_config?.find((cfg) => cfg.nfc_divider) && !tickets.nfc_divider) {
                return schema;
            }

            return [EModule.INVOICE_NFC].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos bilhetes da NFC!") : schema;
        }),
        nfs_points_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (!!upcomingConfig?.points_config?.find((cfg) => cfg.nfs_divider) && !points.nfs_divider) {
                return schema;
            }

            return [EModule.INVOICE_NFS, EModule.REWARD].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos pontos da NFS!") : schema;
        }),
        tickets_from_partial_tax_payment: yup.number().when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.tickets_config?.find((cfg) => cfg.tickets_from_partial_tax_payment) && !tickets.tickets_from_partial_tax_payment) {
                return schema;
            }

            return [EModule.TAXES_AUTO].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor de bilhetes a serem gerados a partir do pagamento parcial!") : schema;
        }),
        tickets_from_full_tax_payment: yup.number().when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.tickets_config?.find((cfg) => cfg.tickets_from_full_tax_payment) && !tickets.tickets_from_full_tax_payment) {
                return schema;
            }

            return [EModule.TAXES_AUTO].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor de bilhetes a serem gerados a partir do pagamento integral!") : schema;
        }),
        generate_ticket_on_first_invoice_import: yup.boolean().default(undefined),
        allow_user_multiple_prizes: yup.boolean().default(false),
        show_winner_name: yup.boolean().default(false),
        show_social_institution_name: yup.boolean().default(false),
        nfc_processing_delay_days: yup.number().default(0),
        nfe_processing_delay_days: yup.number().default(0),
        nfs_processing_delay_days: yup.number().default(0),
        raffle_delay_days: yup.number().default(0),
        nfs_tickets_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.tickets_config?.find((cfg) => cfg.nfs_divider) && !tickets.nfs_divider) {
                return schema;
            }

            return [EModule.INVOICE_NFS].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos bilhetes da NFS!") : schema;
        }),
        taxe_points_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.points_config?.find((cfg) => cfg.taxe_divider) && !points.taxe_divider) {
                return schema;
            }

            return [EModule.TAXES, EModule.REWARD].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos pontos do Imposto!") : schema;
        }),
        taxe_tickets_divider: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            if (upcomingConfig?.tickets_config?.find((cfg) => cfg.taxe_divider) && !tickets.taxe_divider) {
                return schema;
            }

            return [EModule.TAXES].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o valor do divisor dos bilhetes do Imposto!") : schema;
        }),
        nfs_max_amount_tickets: maxAmountSchema(
            EModule.INVOICE_NFS,
            'is_unlimited_nfs_divider_ticket',
            "Por favor, informe o valor máximo para geração de bilhetes a partir de uma NFS!"
        ),
        nfs_max_month_quantity_by_cnpj_tickets: maxQuantityByCnpjSchema(
            EModule.INVOICE_NFS,
            'is_unlimited_by_cnpj_nfs_divider_ticket',
            "Por favor, informe o número máximo mensal de notas em um mesmo estabelecimento para geração de bilhetes a partir de uma NFS!"
        ),
        nfc_max_amount_tickets: maxAmountSchema(
            EModule.INVOICE_NFC,
            'is_unlimited_nfc_divider_ticket',
            "Por favor, informe o valor máximo para geração de bilhetes a partir de uma NFC!"
        ),
        nfc_max_month_quantity_by_cnpj_tickets: maxQuantityByCnpjSchema(
            EModule.INVOICE_NFC,
            'is_unlimited_by_cnpj_nfc_divider_ticket',
            "Por favor, informe o número máximo mensal de notas em um mesmo estabelecimento para geração de bilhetes a partir de uma NFC!"
        ),
        taxe_max_amount_tickets: maxAmountSchema(
            EModule.TAXES,
            'is_unlimited_taxe_divider_ticket',
            "Por favor, informe o valor máximo para geração de bilhetes a partir de um Imposto!"
        ),
        nfs_max_amount_points: maxAmountSchema(
            EModule.REWARD,
            'is_unlimited_nfs_divider_points',
            "Por favor, informe o valor máximo para geração de pontos a partir de uma NFS!"
        ),
        nfs_max_month_quantity_by_cnpj_points: maxQuantityByCnpjSchema(
            EModule.REWARD,
            'is_unlimited_by_cnpj_nfs_divider_points',
            "Por favor, informe o número máximo mensal de notas em um mesmo estabelecimento para geração de pontos a partir de uma NFS!"
        ),
        nfc_max_amount_points: maxAmountSchema(
            EModule.REWARD,
            'is_unlimited_nfc_divider_points',
            "Por favor, informe o valor máximo para geração de pontos a partir de uma NFC!"
        ),
        nfc_max_month_quantity_by_cnpj_points: maxQuantityByCnpjSchema(
            EModule.REWARD,
            'is_unlimited_by_cnpj_nfc_divider_points',
            "Por favor, informe o número máximo mensal de notas em um mesmo estabelecimento para geração de pontos a partir de uma NFC!"
        ),
        taxe_max_amount_points: maxAmountSchema(
            EModule.REWARD,
            'is_unlimited_taxe_divider_points',
            "Por favor, informe o valor máximo para geração de pontos a partir de um Imposto!"
        ),
        time_expire_points: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            return [EModule.REWARD].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o tempo de validade dos pontos!") : schema;
        }),
        time_expire_vouchers: yup.number().positive("Número precisa ser positivo!").when(['modules'], ([modules], schema: any) => {
            return [EModule.REWARD].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o tempo de validade dos Vouchers!") : schema;
        }),
        points_config_effective_date: yup.string().when([
            'nfc_points_divider',
            'nfs_points_divider',
            'taxe_points_divider',
            'nfs_max_amount_points',
            'nfs_max_month_quantity_by_cnpj_points',
            'nfc_max_amount_points',
            'nfc_max_month_quantity_by_cnpj_points',
            'taxe_max_amount_points',
            'is_unlimited_nfc_divider_points',
            'is_unlimited_nfs_divider_points',
            'is_unlimited_taxe_divider_points',
            'is_unlimited_by_cnpj_nfc_divider_points',
            'is_unlimited_by_cnpj_nfs_divider_points',
            'modules',
        ], (
            [
                nfc,
                nfs,
                taxe,
                nfs_max_amount,
                nfs_max_month_quantity,
                nfc_max_amount,
                nfc_max_month_quantity,
                taxe_max_amount,
                isUnlimitedNfc,
                isUnlimitedNfs,
                isUnlimitedTaxe,
                isUnlimitedByCnpjNfc,
                isUnlimitedByCnpjNfs,
                modules,
            ],
            schema: any
        ) => {

            const isCreate = type === "CREATE";

            const currencyValues = {
                nfs_max_amount: nfs_max_amount ? formatCurrencyForPersistence(nfs_max_amount) : undefined,
                nfc_max_amount: nfc_max_amount ? formatCurrencyForPersistence(nfc_max_amount) : undefined,
                taxe_max_amount: taxe_max_amount ? formatCurrencyForPersistence(taxe_max_amount) : undefined,
            }

            const hasInvoiceNFCModule = modules?.includes(EModule.INVOICE_NFC);
            const hasInvoiceNFSModule = modules?.includes(EModule.INVOICE_NFS);
            const hasTaxesModule = modules?.includes(EModule.TAXES);
            const hasRewardsModule = modules?.includes(EModule.REWARD);

            if (!hasRewardsModule) return schema;

            const haveChanged =
                !isCreate &&
                (
                    (points.nfc_divider !== nfc && hasInvoiceNFCModule) ||
                    (points.nfs_divider !== nfs && hasInvoiceNFSModule) ||
                    (points.taxe_divider !== taxe && hasTaxesModule) ||

                    (points.nfs_max_amount !== currencyValues.nfs_max_amount && hasInvoiceNFSModule) ||
                    (points.nfs_max_month_quantity_by_cnpj !== nfs_max_month_quantity && hasInvoiceNFSModule) ||

                    (points.nfc_max_amount !== currencyValues.nfc_max_amount && hasInvoiceNFCModule) ||
                    (points.nfc_max_month_quantity_by_cnpj !== nfc_max_month_quantity && hasInvoiceNFCModule) ||

                    (points.taxe_max_amount !== currencyValues.taxe_max_amount && hasTaxesModule) ||

                    (points.is_unlimited_nfc !== Boolean(isUnlimitedNfc) && hasInvoiceNFCModule) ||
                    (points.is_unlimited_nfs !== Boolean(isUnlimitedNfs) && hasInvoiceNFSModule) ||
                    (points.is_unlimited_taxe !== Boolean(isUnlimitedTaxe) && hasTaxesModule) ||
                    (points.is_unlimited_by_cnpj_nfc !== Boolean(isUnlimitedByCnpjNfc) && hasInvoiceNFCModule) ||
                    (points.is_unlimited_by_cnpj_nfs !== Boolean(isUnlimitedByCnpjNfs) && hasInvoiceNFSModule)
                );

            return haveChanged
                ? schema.required("Por favor, informe a data de vigência dos novos divisores dos pontos!")
                : schema;
        }),
        tickets_config_effective_date: yup.string().when([
            'nfc_tickets_divider',
            'nfs_tickets_divider',
            'taxe_tickets_divider',
            'nfs_max_amount_tickets',
            'nfs_max_month_quantity_by_cnpj_tickets',
            'nfc_max_amount_tickets',
            'nfc_max_month_quantity_by_cnpj_tickets',
            'taxe_max_amount_tickets',
            'is_unlimited_nfc_divider_ticket',
            'is_unlimited_nfs_divider_ticket',
            'is_unlimited_taxe_divider_ticket',
            'is_unlimited_by_cnpj_nfc_divider_ticket',
            'is_unlimited_by_cnpj_nfs_divider_ticket',

            'modules',
        ], (
            [
                nfc,
                nfs,
                taxe,
                nfs_max_amount,
                nfs_max_month_quantity,
                nfc_max_amount,
                nfc_max_month_quantity,
                taxe_max_amount,

                isUnlimitedNfc,
                isUnlimitedNfs,
                isUnlimitedTaxe,
                isUnlimitedByCnpjNfc,
                isUnlimitedByCnpjNfs,

                modules,
            ],
            schema: any
        ) => {

            const isCreate = type === "CREATE";

            const currencyValues = {
                nfs_max_amount: nfs_max_amount ? formatCurrencyForPersistence(nfs_max_amount) : undefined,
                nfc_max_amount: nfc_max_amount ? formatCurrencyForPersistence(nfc_max_amount) : undefined,
                taxe_max_amount: taxe_max_amount ? formatCurrencyForPersistence(taxe_max_amount) : undefined,
            }

            const hasInvoiceNFCModule = modules?.includes(EModule.INVOICE_NFC);
            const hasInvoiceNFSModule = modules?.includes(EModule.INVOICE_NFS);
            const hasTaxesModule = modules?.includes(EModule.TAXES);

            const haveChanged =
                !isCreate &&
                (
                    (tickets.nfc_divider !== nfc && hasInvoiceNFCModule) ||
                    (tickets.nfs_divider !== nfs && hasInvoiceNFSModule) ||
                    (tickets.taxe_divider !== taxe && hasTaxesModule) ||

                    (tickets.nfs_max_amount !== currencyValues.nfs_max_amount && hasInvoiceNFSModule) ||
                    (tickets.nfs_max_month_quantity_by_cnpj !== nfs_max_month_quantity && hasInvoiceNFSModule) ||

                    (tickets.nfc_max_amount !== currencyValues.nfc_max_amount && hasInvoiceNFCModule) ||
                    (tickets.nfc_max_month_quantity_by_cnpj !== nfc_max_month_quantity && hasInvoiceNFCModule) ||

                    (tickets.taxe_max_amount !== currencyValues.taxe_max_amount && hasTaxesModule) ||
                    (tickets.is_unlimited_nfc !== Boolean(isUnlimitedNfc) && hasInvoiceNFCModule) ||
                    (tickets.is_unlimited_nfs !== Boolean(isUnlimitedNfs) && hasInvoiceNFSModule) ||
                    (tickets.is_unlimited_taxe !== Boolean(isUnlimitedTaxe) && hasTaxesModule) ||
                    (tickets.is_unlimited_by_cnpj_nfc !== Boolean(isUnlimitedByCnpjNfc) && hasInvoiceNFCModule) ||
                    (tickets.is_unlimited_by_cnpj_nfs !== Boolean(isUnlimitedByCnpjNfs) && hasInvoiceNFSModule)
                );

            return haveChanged
                ? schema.required("Por favor, informe a data de vigência dos novos divisores dos bilhetes!")
                : schema;
        }),
        terms_of_use_effective_date: yup.string().when(['terms_of_use'], ([terms], schema: any) => {
            const isCreate = type === "CREATE";
            const hasNew = !isCreate && Boolean(terms);

            return hasNew ? schema.required("Por favor, informe a data de vigência dos novos Termos de Uso!") : schema;
        }),
        status: yup.boolean(),
        ...(type === "CREATE" ? {
            modules: yup.array().of(yup.mixed<EModule>().required()).test('atLestOne', 'Por favor, selecione ao menos um módulo de arrecadação.', (modules) => modules?.some((module) => [EModule.INVOICE_NFC, EModule.INVOICE_NFS, EModule.TAXES].includes(module))),
            system_id: yup.number().when(['modules'], ([modules], schema: any) => {
                return [EModule.INVOICE_NFS].every((module) => modules.includes(module)) ? schema.required("Por favor, informe o sistema de Nota Fiscal de Serviço") : schema.nullable();
            }),
        } : {
            modules: yup.array().of(yup.mixed<EModule>().required()),
            system_id: yup.number().when(['modules'], ([modules], schema: any) => {
                return [EModule.INVOICE_NFS].every((module) => modules.includes(module)) && !cityModules.includes(EModule.INVOICE_NFS) ? schema.required("Por favor, informe o sistema de Nota Fiscal de Serviço") : schema.nullable();
            }),
        }),
        primary_color: yup.string().required("Por favor, informe a cor primária para customização!"),
        secondary_color: yup.string().required("Por favor, informe a cor secundária para customização!"),
        font_color: yup.string().required("Por favor, informe a cor da fonte para customização!"),
        current_raffle_color: yup.string().required("Por favor, informe a cor do componente 'sorteio atual' para customização!"),
        next_raffle_color: yup.string().required("Por favor, informe a cor do componente 'próximo sorteio' para customização!"),
        is_unlimited_nfs_divider_points: yup.boolean().default(undefined),
        is_unlimited_nfc_divider_points: yup.boolean().default(undefined),
        is_unlimited_by_cnpj_nfc_divider_points: yup.boolean().default(undefined),
        is_unlimited_by_cnpj_nfs_divider_points: yup.boolean().default(undefined),
        is_unlimited_taxe_divider_points: yup.boolean().default(undefined),
        is_unlimited_nfs_divider_ticket: yup.boolean().default(undefined),
        is_unlimited_nfc_divider_ticket: yup.boolean().default(undefined),
        is_unlimited_by_cnpj_nfc_divider_ticket: yup.boolean().default(undefined),
        is_unlimited_by_cnpj_nfs_divider_ticket: yup.boolean().default(undefined),
        is_unlimited_taxe_divider_ticket: yup.boolean().default(undefined),
    });
